
import React from 'react';
import { SectionTitle } from './SectionTitle';
import { ScrollAnimation } from './ScrollAnimation';
import { FlagBRIcon } from './icons/FlagBRIcon';
import { FlagUSIcon } from './icons/FlagUSIcon';
import { FlagCAIcon } from './icons/FlagCAIcon';
import { FlagMXIcon } from './icons/FlagMXIcon';
import { FlagCOIcon } from './icons/FlagCOIcon';
import { FlagCLIcon } from './icons/FlagCLIcon';
import { FlagSEIcon } from './icons/FlagSEIcon';
import { FlagGBIcon } from './icons/FlagGBIcon';
import { FlagFRIcon } from './icons/FlagFRIcon';
import { FlagDEIcon } from './icons/FlagDEIcon';
import { FlagPLIcon } from './icons/FlagPLIcon';
import { FlagIEIcon } from './icons/FlagIEIcon';
import { FlagITIcon } from './icons/FlagITIcon';
import { FlagESIcon } from './icons/FlagESIcon';
import { FlagTRIcon } from './icons/FlagTRIcon';
import { FlagKRIcon } from './icons/FlagKRIcon';
import { FlagJPIcon } from './icons/FlagJPIcon';
import { CifraClubPhoto } from './CifraClubPhoto';
import { FiverrIcon } from './icons/FiverrIcon';
import { MicrophoneIcon } from './icons/MicrophoneIcon';
import { SpotlightCard } from './SpotlightCard';
import { MagneticEffect } from './MagneticEffect';
import { useTranslation } from '../i18n/LanguageContext';


interface Country {
  name: string;
  code: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
}

const countriesData: Country[] = [
  { name: 'United States', code: 'US', Icon: FlagUSIcon },
  { name: 'Canada', code: 'CA', Icon: FlagCAIcon },
  { name: 'Mexico', code: 'MX', Icon: FlagMXIcon },
  { name: 'Colombia', code: 'CO', Icon: FlagCOIcon },
  { name: 'Brazil', code: 'BR', Icon: FlagBRIcon },
  { name: 'Chile', code: 'CL', Icon: FlagCLIcon },
  { name: 'Sweden', code: 'SE', Icon: FlagSEIcon },
  { name: 'United Kingdom', code: 'GB', Icon: FlagGBIcon },
  { name: 'France', code: 'FR', Icon: FlagFRIcon },
  { name: 'Germany', code: 'DE', Icon: FlagDEIcon },
  { name: 'Poland', code: 'PL', Icon: FlagPLIcon },
  { name: 'Ireland', code: 'IE', Icon: FlagIEIcon },
  { name: 'Italy', code: 'IT', Icon: FlagITIcon },
  { name: 'Spain', code: 'ES', Icon: FlagESIcon },
  { name: 'Turkey', code: 'TR', Icon: FlagTRIcon },
  { name: 'South Korea', code: 'KR', Icon: FlagKRIcon },
  { name: 'Japan', code: 'JP', Icon: FlagJPIcon },
];

const highlightsIcons = {
    cifraclub: CifraClubPhoto,
    fiverr: FiverrIcon,
    musician: MicrophoneIcon,
};


export const AboutSection: React.FC = () => {
  const { t } = useTranslation();
  // Triplicar para garantir que o carrossel nunca mostre espaços vazios
  const marqueeCountries = [...countriesData, ...countriesData, ...countriesData];

  const highlights = t('about.highlights') as { key: keyof typeof highlightsIcons; title: string; description: string; logoClassName: string }[];

  return (
    <section id="sobre-mim" className="py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollAnimation animation="slideInUp">
            <SectionTitle title={t('about.title')} />
        </ScrollAnimation>
        <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 lg:gap-16">
          <ScrollAnimation animation="slideInUp" delay={200} className="md:w-1/2 lg:w-2/5 flex-shrink-0">
            <MagneticEffect strength={30}>
              <img
                src="https://nikolasyuri.com.br/wp/wp-content/uploads/2025/06/Nikolas-600x800-1.jpg"
                alt={`${t('instructorName')} - Professor de Piano e Teclado`}
                className="rounded-xl shadow-2xl w-full max-w-sm mx-auto md:max-w-none object-cover border border-slate-700/60"
              />
            </MagneticEffect>
          </ScrollAnimation>
          <div className="w-full md:w-1/2 lg:w-3/5 text-brand-text text-base sm:text-lg space-y-4 sm:space-y-5">
            <ScrollAnimation animation="slideInUp" delay={300}>
              <p className="break-words" dangerouslySetInnerHTML={{ __html: t('about.paragraph1') }} />
            </ScrollAnimation>
            <ScrollAnimation animation="slideInUp" delay={400}>
              <p className="break-words" dangerouslySetInnerHTML={{ __html: t('about.paragraph2') }} />
            </ScrollAnimation>
            
            <ScrollAnimation animation="slideInUp" delay={500} className="mt-8 md:mt-10">
              <h3 className="text-xl sm:text-2xl font-serif font-semibold text-brand-light mb-6">
                {t('about.highlightsTitle')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {highlights.map((highlight, index) => {
                  const Icon = highlightsIcons[highlight.key];
                  return (
                    <ScrollAnimation key={index} animation="slideInUp" delay={600 + index * 100}>
                      <SpotlightCard className="bg-brand-primary/60 p-6 rounded-lg shadow-xl border border-slate-600/50 hover:border-brand-accent/40 transition-all duration-300 transform hover:-translate-y-1 h-full flex flex-col items-center justify-center text-center">
                          <Icon className={`${highlight.logoClassName} mb-4 text-brand-light opacity-90`} />
                          <h4 className="text-lg font-serif font-semibold text-brand-light mb-2">{highlight.title}</h4>
                          <p className="text-brand-text text-sm">{highlight.description}</p>
                      </SpotlightCard>
                    </ScrollAnimation>
                  );
                })}
              </div>
            </ScrollAnimation>

            <ScrollAnimation animation="fadeIn" delay={800}>
              <div className="mt-8 w-full overflow-hidden">
                <p className="text-sm sm:text-md text-brand-light text-center mb-4 font-semibold break-words">
                  {t('about.countriesPrompt')}
                </p>
                {/* Container com overflow-hidden explícito para isolar a marquee */}
                <div className="relative w-full overflow-hidden marquee-container">
                  <div className="animate-marquee flex gap-4 py-2">
                    {marqueeCountries.map((country, index) => (
                      <div 
                        key={`${country.code}-${index}`} 
                        className="group relative flex-shrink-0"
                        title={country.name} 
                      >
                        <country.Icon className="w-10 h-auto sm:w-12 rounded-sm shadow-md transition-transform duration-300 group-hover:scale-110" />
                        <span 
                          className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max px-2 py-1 
                                     text-xs text-white bg-slate-800 rounded-md opacity-0 
                                     group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10"
                        >
                          {country.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollAnimation>
            
            <ScrollAnimation animation="slideInUp" delay={900}>
              <p className="font-semibold text-brand-light text-base sm:text-lg mt-8 break-words">
                {t('about.mission')}
              </p>
            </ScrollAnimation>
          </div>
        </div>
      </div>
    </section>
  );
};
